import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";


// All before/after projects from the uploaded files
const projects = [
  {
    id: 1,
    title: "Deck Staining & Restoration",
    before: "/projects/1/before.webp",
    after: "/projects/1/after.webp",
  },
  {
    id: 2,
    title: "Exterior Wood Painting",
    before: "/projects/2/before.webp",
    after: "/projects/2/after.webp",
  },
  {
    id: 3,
    title: "French Door Painting",
    before: "/projects/3/before.webp",
    after: "/projects/3/after.webp",
  },
  {
    id: 4,
    title: "Interior Door Painting",
    before: "/projects/4/before.webp",
    after: "/projects/4/after.webp",
  },
  {
    id: 5,
    title: "Railing Staining & Painting",
    before: "/projects/5/before.webp",
    after: "/projects/5/after.webp",
  },
  {
    id: 6,
    title: "Exterior Siding Painting",
    before: "/projects/6/before.png",
    after: "/projects/6/after.png",
  },
  {
    id: 7,
    title: "Window Frame Painting",
    before: "/projects/7/before.png",
    after: "/projects/7/after.png",
  },
  {
    id: 8,
    title: "Trim & Molding Painting",
    before: "/projects/8/before.png",
    after: "/projects/8/after.png",
  },
  {
    id: 9,
    title: "Porch Painting & Staining",
    before: "/projects/9/before.png",
    after: "/projects/9/after.png",
  },
  {
    id: 10,
    title: "Cabinet Refinishing",
    before: "/projects/10/before.png",
    after: "/projects/10/after.png",
  },
  {
    id: 11,
    title: "Fence Staining",
    before: "/projects/11/before.png",
    after: "/projects/11/after.png",
  },
  {
    id: 12,
    title: "Stair Painting & Refinishing",
    before: "/projects/12/before.png",
    after: "/projects/12/after.png",
  },
  {
    id: 13,
    title: "Exterior House Painting",
    before: "/projects/13/before.jpeg",
    after: "/projects/13/after.png",
  },
];

export const PortfolioPage = () => {
  const [activeProject, setActiveProject] = useState(null);

  return (
    <div className="bg-[#f8f9fa]" data-testid="portfolio-page">
      {/* Hero Banner */}
      <section className="bg-[#0c2340] pt-28 sm:pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Our Portfolio
          </h1>
          <p className="text-gray-400 text-base sm:text-lg">
            {projects.length} completed projects showcasing our craftsmanship
          </p>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div 
                key={project.id} 
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group cursor-pointer"
                onClick={() => setActiveProject(project)}
                data-testid={`project-${project.id}`}
              >
                <div className="relative overflow-hidden">
                  <BeforeAfterSlider
                    beforeImage={project.before}
                    afterImage={project.after}
                    className="aspect-[4/3]"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-bold text-[#1a1a2e]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {project.title}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">Drag slider to compare</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#f97316]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Transform Your Home?
          </h2>
          <p className="text-white/90 mb-8 text-sm sm:text-base">
            Get a free estimate for your next project
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact">
              <Button className="w-full sm:w-auto bg-white text-[#f97316] hover:bg-gray-100 font-bold rounded-lg px-8 py-6">
                Get a Free Quote <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="tel:2368681195" className="w-full sm:w-auto">
              <Button variant="outline" className="w-full border-2 border-white text-white hover:bg-white/10 font-bold rounded-lg px-8 py-6">
                <Phone className="w-5 h-5 mr-2" /> (236)868-1195
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Modal for expanded view */}
      {activeProject && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setActiveProject(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-xl font-bold text-[#1a1a2e]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {activeProject.title}
              </h3>
              <button 
                onClick={() => setActiveProject(null)}
                className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <BeforeAfterSlider
                beforeImage={activeProject.before}
                afterImage={activeProject.after}
                className="aspect-video rounded-lg overflow-hidden"
              />
              <p className="text-center text-gray-500 mt-4">Drag the slider to see the transformation</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioPage;
